import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  WbsItemStatus,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { useSearchConditionAndColumnStateV2StorageService } from '../../../../services/storage-services/ui-states/searchConditionAndColumnStateV2StorageService'
import { useWbsItemSearchConditionUrlTransformService } from '../../../../services/transform-service/wbsItemSearchConditionUrlTransformService'
import { useWbsItemSearchConditionV2Factory } from '../../../../services/factories/wbsItemSearchConditionV2Factory'
import { getUrlQueryObject } from '../../../../utils/urls'
import { WbsItemSearchSearchConditionV2 } from './useSearchConditionV2'
import { URL_PARAMS_SHARE_SAVED_UI_STATE } from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { WbsItemSearchConditionV2StorageService } from '../../../../services/storage-services/ui-states/wbsItemSearchConditionV2StorageService'
import { useExcludeInaccessibleRootUuids } from './useExcludeInaccessibleRootUuids'

const wbsItemSearchDefaultSearchCondition: WbsItemSearchSearchConditionV2 = {
  types: [WbsItemType.TASK],
  status: [
    WbsItemStatus.TODO,
    WbsItemStatus.DOING,
    WbsItemStatus.REVIEW,
    WbsItemStatus.DONE,
  ],
}

export type SearchConditionRestoreType =
  | 'PERMANENT'
  | 'URL_QUERY'
  | 'UI_STATE'
  | 'DID_NOT_RESTORE'

export const useRestoredSearchConditionV2 = (
  functionUuid: string,
  projectUuid: string,
  uiStateStorageService: WbsItemSearchConditionV2StorageService
) => {
  const [initialized, setInitialized] = useState<boolean>(false)
  const [restoredSearchCondition, setRestoredSearchCondition] =
    useState<WbsItemSearchSearchConditionV2>(
      wbsItemSearchDefaultSearchCondition
    )
  const [restoreType, setRestoreType] =
    useState<SearchConditionRestoreType>('DID_NOT_RESTORE')

  const searchConditionStorageService =
    useSearchConditionAndColumnStateV2StorageService(functionUuid, projectUuid)
  const wbsItemSearchConditionUrlTransformer =
    useWbsItemSearchConditionUrlTransformService()
  const searchConditionV2Factory = useWbsItemSearchConditionV2Factory()
  const { exclude: excludeInaccessibleRootUuids, notify: notifyExcluded } =
    useExcludeInaccessibleRootUuids(functionUuid, projectUuid)

  const restorePermanentUiState = useCallback(async () => {
    const urlObject = getUrlQueryObject()
    if (!urlObject) return undefined
    const code = urlObject.arg
    if (!code) return undefined
    const permanentUiState = await searchConditionStorageService.getByCode(code)
    if (!permanentUiState) return undefined
    return permanentUiState
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchConditionStorageService.getByCode])

  const restoreFromURLQuery = useCallback(() => {
    const conditionFromUrl =
      wbsItemSearchConditionUrlTransformer.restoreFromUrl()
    // URL_PARAMS_SHARE_SAVED_UI_STATE は検索条件のパラメータではないため除外する
    delete conditionFromUrl[URL_PARAMS_SHARE_SAVED_UI_STATE]
    // V1/V2切り替えパラメータ。V2統合後も既存ブックマークURLとの互換性維持のため削除を継続
    delete conditionFromUrl['usev2']
    if (!conditionFromUrl || Object.keys(conditionFromUrl).length === 0) {
      return undefined
    }
    const rootUuid = conditionFromUrl.rootUuid
    const vo = searchConditionV2Factory.fromApiResponseToVo(conditionFromUrl)
    vo.rootUuids = rootUuid ? [rootUuid] : undefined
    return vo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const restoreFromUiState = useCallback(async () => {
    const storedSearchCondition = await uiStateStorageService.fetch(
      functionUuid,
      projectUuid
    )
    if (!storedSearchCondition) {
      return undefined
    }
    return storedSearchCondition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionUuid, projectUuid])

  useEffect(() => {
    const restore = async () => {
      const permanentUiState = await restorePermanentUiState()
      if (permanentUiState) {
        const { cleaned, excluded } = await excludeInaccessibleRootUuids(
          permanentUiState.value.searchCondition
        )
        if (excluded) {
          await searchConditionStorageService.save({
            uuid: permanentUiState.uuid,
            name: permanentUiState.name,
            scope: permanentUiState.scope,
            value: {
              searchCondition: cleaned,
              columnState: permanentUiState.value.columnState,
            },
          })
          notifyExcluded()
        }
        setRestoredSearchCondition(cleaned)
        setRestoreType('PERMANENT')
        setInitialized(true)
        return
      }
      const searchConditionInURLQuery = restoreFromURLQuery()
      if (searchConditionInURLQuery) {
        const { cleaned, excluded } = await excludeInaccessibleRootUuids(
          searchConditionInURLQuery
        )
        if (excluded) {
          notifyExcluded()
        }
        setRestoredSearchCondition(cleaned)
        setRestoreType('URL_QUERY')
        setInitialized(true)
        return
      }
      const storedSearchCondition = await restoreFromUiState()
      if (storedSearchCondition) {
        const { cleaned, excluded } = await excludeInaccessibleRootUuids(
          storedSearchCondition
        )
        if (excluded) {
          await uiStateStorageService.save(functionUuid, projectUuid, cleaned)
          notifyExcluded()
        }
        setRestoredSearchCondition(cleaned)
        setRestoreType('UI_STATE')
        setInitialized(true)
        return
      }
      setInitialized(true)
    }
    restore()
    // 初期復元は1回のみ実行する。除外処理や保存サービスへの依存を加えると、
    // ユーザーが編集中の検索条件を上書きする再実行が起きうるため意図的に依存を限定している。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restorePermanentUiState, restoreFromURLQuery, restoreFromUiState])

  const result = useMemo(
    () => ({
      initialized,
      restoredSearchCondition,
      restoreType,
    }),
    [initialized, restoredSearchCondition, restoreType]
  )
  return result
}
